/** Боковая панель «Карточка сущности» (drill-through): справа поверх любой вкладки страницы,
 *  адрес `card=<вид>:<ключ>` (state.ts). Панель — общий каркас `components/EntityPanelFrame`
 *  (подложка, шапка с ✕, своя прокрутка, фокус, Esc; общий с «Аналитикой ОПТ»); содержимое — по
 *  виду карточки (`CARD_VIEWS`). */
import type React from "react";
import { useLocation, useNavigate } from "react-router-dom";

import type { OzFilters } from "@/api/ozonAnalytics";
import { EntityPanelFrame } from "@/components/EntityPanelFrame";
import { ArticleCard } from "./ArticleCard";
import { CampaignCard } from "./CampaignCard";
import { CARD_BACK_STATE, type CardKind, type CardRef } from "./state";
import { SupplyCard } from "./SupplyCard";

/** Содержимое карточки вида: `titleId` — id заголовка (aria-labelledby панели). */
interface CardViewProps {
  entityKey: string;
  filters: OzFilters;
  warning: string | null;
  titleId: string;
  /** переход к другой карточке внутри панели (новая запись истории, «Назад» — обратно) */
  onOpenCard: (card: CardRef) => void;
}

/** Вид карточки → подпись шапки и содержимое. Новый вид — строка здесь и код в `CARD_KINDS`. */
const CARD_VIEWS: Record<
  CardKind,
  { label: string; View: (p: CardViewProps) => React.ReactElement }
> = {
  article: {
    label: "Карточка артикула",
    View: ({ entityKey, filters, warning, titleId, onOpenCard }) => (
      <ArticleCard
        offerId={entityKey}
        filters={filters}
        warning={warning}
        titleId={titleId}
        onOpenCard={onOpenCard}
      />
    ),
  },
  supply: {
    label: "Карточка поставки",
    View: ({ entityKey, filters, warning, titleId, onOpenCard }) => (
      <SupplyCard
        orderNumber={entityKey}
        filters={filters}
        warning={warning}
        titleId={titleId}
        onOpenCard={onOpenCard}
      />
    ),
  },
  campaign: {
    label: "Карточка рекламной кампании",
    View: ({ entityKey, filters, warning, titleId, onOpenCard }) => (
      <CampaignCard
        campaignId={entityKey}
        filters={filters}
        warning={warning}
        titleId={titleId}
        onOpenCard={onOpenCard}
      />
    ),
  },
};

export function EntityPanel({
  card,
  filters,
  cmpWarning,
  onClose,
  onOpenCard,
}: {
  card: CardRef;
  filters: OzFilters;
  /** Δ ненадёжна (сравнение вне данных витрины) — ⚠ у Δ, как на странице */
  cmpWarning: string | null;
  onClose: () => void;
  /** открыть другую карточку из этой (артикул → поставка): запись истории с CARD_BACK_STATE */
  onOpenCard: (card: CardRef) => void;
}): React.ReactElement {
  // карточку открыли из другой карточки — «Назад» в шапке (= «Назад» браузера) вернёт к ней
  const location = useLocation();
  const navigate = useNavigate();
  const canBack =
    (location.state as Record<string, unknown> | null)?.[CARD_BACK_STATE] === true;
  const { label, View } = CARD_VIEWS[card.kind];

  return (
    <EntityPanelFrame
      label={label}
      cardId={`${card.kind}:${card.key}`}
      onClose={onClose}
      onBack={canBack ? () => navigate(-1) : undefined}
    >
      {(titleId) => (
        <View
          entityKey={card.key}
          filters={filters}
          warning={cmpWarning}
          titleId={titleId}
          onOpenCard={onOpenCard}
        />
      )}
    </EntityPanelFrame>
  );
}
